import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCustomAlert, useErrorToast } from '../../components/common';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import useApi from '../../hooks/useApi';
import { getApiErrorMessage } from '../../utils/errorMessages';

const RoleUsersDialog = ({ open, role, onClose }) => {
  const api = useApi();
  const apiRef = useRef(api);
  const { showAlert, showConfirm } = useCustomAlert();
  const { showErrorToast } = useErrorToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [removeLoading, setRemoveLoading] = useState(null);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const loadUsers = useCallback(async () => {
    if (!role?.roleId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiRef.current.get(`/roles/${role.roleId}/users`, {
        params: {
          page: page + 1,
          pageSize: rowsPerPage,
        },
      });

      const apiResponse = response.data;

      if (apiResponse?.success && apiResponse?.data) {
        setUsers(apiResponse.data.users || []);
        setTotalUsers(apiResponse.data.pagination?.totalItems || 0);
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load users'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [role?.roleId, page, rowsPerPage]);

  useEffect(() => {
    if (open && role) {
      setPage(0);
      loadUsers();
    }
  }, [open, role]);

  useEffect(() => {
    if (open && role) {
      loadUsers();
    }
  }, [page, rowsPerPage]);

  const handleRemoveUser = async (user) => {
    const confirmed = await showConfirm(`Remove "${user.fullName || user.email}" from role "${role.roleName}"?`, { title: 'Remove User', confirmText: 'Remove' });
    if (!confirmed) return;

    try {
      setRemoveLoading(user.userId);
      await apiRef.current.delete(`/roles/${role.roleId}/users/${user.userId}`, {
        params: { tenantId: user.tenantId }
      });
      loadUsers();
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, 'Failed to remove user'));
    } finally {
      setRemoveLoading(null);
    }
  };

  const handleClose = () => {
    setUsers([]);
    setError(null);
    setPage(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6">Users with Role: {role?.roleName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {totalUsers} user{totalUsers !== 1 ? 's' : ''} assigned
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <Typography color="text.secondary">No users assigned to this role.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow key={`${user.userId}-${user.tenantId}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {user.fullName || user.displayName || 'N/A'}
                        </Typography>
                        {user.isJLLUser && (
                          <Chip label="JLL" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.tenantName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove from role">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveUser(user)}
                            disabled={removeLoading === user.userId}
                          >
                            {removeLoading === user.userId ? (
                              <CircularProgress size={18} />
                            ) : (
                              <PersonRemoveIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button onClick={handleClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleUsersDialog;


